import { useState } from 'react';
import { LESSONS, lessonsByBlock } from '../../content/lessons.js';
import { PRACTICE_TEXTS } from '../../content/texts.js';
import { modeLabel } from '../../content/reference-modes.js';
import { store, useStoreRevision } from '../store';

/** Zoznam lekcií po blokoch s hviezdičkami a zvýraznením ďalšej lekcie. */
export function Lessons() {
  useStoreRevision();
  const blocks = lessonsByBlock();
  const completed = store.completedLessonIds();
  const nextLesson = LESSONS.find((lesson) => !completed.includes(lesson.id)) ?? null;

  return (
    <>
      <div className="card">
        <h2>Lekcie</h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          Každá lekcia používa len klávesy z predchádzajúcich lekcií. Cieľom nie je rýchlosť,
          ale presnosť — až keď ide text bez chýb, zrýchľuje sa sám.
        </p>
        {nextLesson && (
          <a className="btn btn-primary" href={`#/cvicenie/${nextLesson.id}`}>
            Pokračovať: lekcia {nextLesson.number} · {nextLesson.title}
          </a>
        )}
      </div>

      {blocks.map((block) => (
        <section className="block" key={block.block}>
          <h3 className="block-title">
            <span>{block.block}. {block.title}</span>
            <span className="muted small">
              · {block.lessons.length} lekcií · cieľ {block.lessons[0].targets.cpm} úhozov/min
            </span>
          </h3>
          <p className="block-note">{block.note}</p>

          {block.lessons.map((lesson) => {
            const isDone = completed.includes(lesson.id);
            const stars = store.stars(lesson.id);
            const best = store.best(lesson.id);
            return (
              <div className={`lesson-row ${isDone ? 'is-done' : ''}`} key={lesson.id}>
                <a className="lesson-title" href={`#/cvicenie/${lesson.id}`}>
                  {lesson.number}. {lesson.title}
                </a>
                <span className="lesson-meta">
                  {lesson.newKeys.length > 0 && <>nové: {lesson.newKeys.join(' ')} · </>}
                  {lesson.interpreted && <>obsah je odhad · </>}
                  {best && (
                    <>
                      najlepšie {best.netCpm} úderov/min · {Math.round(best.accuracy * 100)} % ·{' '}
                    </>
                  )}
                  <span className="stars" aria-label={stars > 0 ? `${stars} z 3 hviezdičiek` : 'bez hodnotenia'}>
                    {stars > 0 ? '★'.repeat(stars) : '·'}
                  </span>
                </span>
                <span className="lesson-modes">
                  {lesson.modes.map((mode) => (
                    <a key={mode} className="chip" href={`#/cvicenie/${lesson.id}/${mode}`}>
                      {modeLabel(mode)}
                    </a>
                  ))}
                </span>
              </div>
            );
          })}
        </section>
      ))}
    </>
  );
}

/** Zoznam cvičných textov + možnosť vložiť vlastný text. */
export function TextsView({ onStartCustom }: { onStartCustom: (text: string) => void }) {
  const [custom, setCustom] = useState('');

  return (
    <>
      <div className="card">
        <h2>Cvičné texty</h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          Súvislé texty na rýchlosť. Všetky sú vlastné a uvoľnené ako CC0 — zámerne tu nie sú
          texty z iných výučbových stránok, tie sú chránené autorským právom.
        </p>
        {PRACTICE_TEXTS.map((text) => (
          <a className="lesson-row" key={text.id} href={`#/text/${text.id}`}>
            <span className="lesson-title">{text.title}</span>
            <span className="lesson-meta">
              {text.difficulty} · {text.paragraphs.join(' ').length} znakov ·{' '}
              {text.diacritics ? 's diakritikou' : 'bez diakritiky'}
            </span>
          </a>
        ))}
      </div>

      <div className="card">
        <h2>Vlastný text</h2>
        <p className="muted small">
          Vlož text, ktorý chceš precvičiť — poznámky, básničku, učivo. Text zostáva v tvojom
          prehliadači a nikam sa neposiela.
        </p>
        <label className="visually-hidden" htmlFor="custom-text">Vlastný text</label>
        <textarea
          id="custom-text"
          value={custom}
          onChange={(event) => setCustom(event.target.value)}
          rows={6}
          style={{ width: '100%', fontFamily: 'var(--mono)', padding: 10, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)' }}
          placeholder="Sem vlož alebo napíš text…"
        />
        <div className="row" style={{ marginTop: 10 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={custom.trim().length < 10}
            onClick={() => onStartCustom(custom)}
          >
            Cvičiť na tomto texte
          </button>
          <span className="muted small">Aspoň 10 znakov.</span>
        </div>
      </div>
    </>
  );
}
